import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Feed from './pages/Feed';
import DeleteAccount from './pages/DeleteAccount';
import Camera from './pages/Camera';
import Settings from './pages/Settings';
import Verifications from './pages/Verifications';
import PhotoView from './pages/PhotoView';
import Notifications from './pages/Notifications';

// Components
import BottomNav from './components/BottomNav';

// API
import { getMe } from './api/auth';
import { getNotificationCount, markNotificationsSeen } from './api/notifications';
import { getPendingVerifications, confirmVerification } from './api/verification';
import { saveLocationToHistory, verifyAttributesLocally } from './services/attributeMatcher';
import { storeProximitySnapshot } from './services/proximityService';

const POLL_INTERVAL = 30000; // 30 seconds
const LOCATION_TRACK_INTERVAL = 60000; // 1 minute

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [newPhotosCount, setNewPhotosCount] = useState(0);
  const [pendingVerificationsCount, setPendingVerificationsCount] = useState(0);

  // Background location tracking for proximity verification
  const trackLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Save to local history (never sent to server)
        saveLocationToHistory(latitude, longitude);
        // Store full proximity snapshot with WiFi/Bluetooth
        storeProximitySnapshot(latitude, longitude);
      },
      (error) => {
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  // Auto-verify pending shares in background
  const autoVerifyPendingShares = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await getPendingVerifications();
      const pending = data.verifications || [];
      setPendingVerificationsCount(pending.length);
      
      if (pending.length === 0) return;
      
      // Try to auto-verify each pending share
      for (const verification of pending) {
        try {
          const result = await verifyAttributesLocally(verification.attributes);
          
          if (result.overall_match) {
            // Confirm with server
            const response = await confirmVerification(verification.share_id, result);
            
            if (response.photo_shared) {
              toast.success(`📸 Photo from ${verification.owner_name} verified and added to your feed!`);
              setPendingVerificationsCount(prev => Math.max(0, prev - 1));
            }
          }
        } catch (err) {
        }
      }
    } catch (error) {
    }
  }, [isAuthenticated]);

  // Poll for new notifications
  const checkNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await getNotificationCount();
      if (data.new_photos > 0 && data.new_photos !== newPhotosCount) {
        // Show toast only if count increased
        if (data.new_photos > newPhotosCount && newPhotosCount >= 0) {
          toast.info(`📸 You have ${data.new_photos} new photo${data.new_photos > 1 ? 's' : ''}!`, {
            action: {
              label: 'View',
              onClick: () => window.location.href = '/feed'
            }
          });
        }
        setNewPhotosCount(data.new_photos);
      } else {
        setNewPhotosCount(data.new_photos);
      }
    } catch (error) {
    }
  }, [isAuthenticated, newPhotosCount]);

  useEffect(() => {
    loadUser();
  }, []);

  // Start polling when user is logged in
  useEffect(() => {
    if (isAuthenticated && user?.enrollment_complete) {
      // Initial checks
      checkNotifications();
      autoVerifyPendingShares();
      trackLocation();
      
      // Poll notifications periodically
      const notificationInterval = setInterval(checkNotifications, POLL_INTERVAL);
      
      // Poll pending verifications periodically
      const verificationInterval = setInterval(autoVerifyPendingShares, POLL_INTERVAL);
      
      // Track location for proximity verification
      const locationInterval = setInterval(trackLocation, LOCATION_TRACK_INTERVAL);
      
      // Also check when window regains focus
      const handleFocus = () => {
        checkNotifications();
        autoVerifyPendingShares();
        trackLocation();
      };
      window.addEventListener('focus', handleFocus);
      
      return () => {
        clearInterval(notificationInterval);
        clearInterval(verificationInterval);
        clearInterval(locationInterval);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [isAuthenticated, user?.enrollment_complete, checkNotifications, autoVerifyPendingShares, trackLocation]);

  const loadUser = async () => {
    try {
      const userData = await getMe();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (_tokenIgnored, userData) => {
    // Token is now in httpOnly cookie — no localStorage needed
    setIsAuthenticated(true);
    setUser(userData);
    setNewPhotosCount(0);
  };

  const handleLogout = async () => {
    const { logout } = await import('./api/auth');
    await logout();
    setIsAuthenticated(false);
    setUser(null);
    setNewPhotosCount(0);
    setPendingVerificationsCount(0);
  };

  // Call this when user views the feed to clear the badge
  const handleFeedViewed = async () => {
    // Always try to mark as seen and reset local count
    try {
      await markNotificationsSeen();
      setNewPhotosCount(0);
    } catch (error) {
      // Still reset local count even if API fails
      setNewPhotosCount(0);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <Toaster position="top-center" richColors />
        
        <Routes>
          {/* Public Routes */}
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/login" element={
            !isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/feed" />
          } />
          <Route path="/register" element={
            !isAuthenticated ? <Register onRegister={handleLogin} /> : <Navigate to="/feed" />
          } />

          {/* Protected Routes */}
          <Route path="/onboarding" element={
            isAuthenticated ? (
              <Onboarding user={user} onComplete={loadUser} />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/feed" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Feed user={user} onFeedViewed={handleFeedViewed} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/camera" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Camera user={user} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/verifications" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Verifications user={user} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/inbox" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Notifications onViewed={handleFeedViewed} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/settings" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
          
          <Route path="/photo/:photoId" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <PhotoView user={user} /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />

          {/* Default Route */}
          <Route path="/" element={
            isAuthenticated ? (
              user?.enrollment_complete ? <Navigate to="/feed" /> : <Navigate to="/onboarding" />
            ) : <Navigate to="/login" />
          } />
        </Routes>

        {/* Bottom Navigation - only show after enrollment complete */}
        {isAuthenticated && user?.enrollment_complete && (
          <BottomNav newPhotosCount={newPhotosCount} pendingVerificationsCount={pendingVerificationsCount} />
        )}
      </div>
    </Router>
  );
}

export default App;
